import { useState, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
} from '@mui/material'
import {
  MonitorOutlined,
  AppsOutlined,
  Link as LinkIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material'
import api from '../api/client'
import { usePreferencesStore } from '../stores/preferencesStore'
import { usePortalStore } from '../stores/portalStore'

interface AppItem {
  id: string
  name: string
  technical_name?: string
  icon_url?: string
  app_type: string
  description?: string
  farm_id?: string
  path?: string
  folder_name?: string
}

interface LaunchResult {
  mode: string
  download_url?: string
  html5_url?: string
  redirect_url?: string
  file_name?: string
  server?: string
  username?: string
  password_encrypted?: string
  initial_program?: string
  working_dir?: string
  width?: number
  height?: number
  dpi?: number
  app_name?: string
  guacamole_host?: string
}

export default function AppsPage() {
  const { t } = useTranslation()
  const { toggleFavorite, isFavorite, launchMode, appIconSize: userIconSize } = usePreferencesStore()
  const { config } = usePortalStore()
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<'all' | 'favorites'>('all')
  const [launchingId, setLaunchingId] = useState<string | null>(null)
  const [launchError, setLaunchError] = useState('')

  const glassmorphism = (config?.glassmorphism ?? false) && config?.backgroundMode === 'image'
  const iconMode = config?.iconMode || 'generic'
  const appIconSize = userIconSize || config?.appIconSize || 'large'

  const isSmallSize = appIconSize === 'small'

  const {
    data: apps = [],
    isLoading,
    error,
    refetch,
  } = useQuery<AppItem[]>({
    queryKey: ['apps', 'catalog'],
    queryFn: async () => {
      const res = await api.get('/apps/catalog')
      return res.data
    },
  })

  const filteredApps = apps.filter((app) => {
    const q = search.toLowerCase()
    const matchesSearch =
      !search ||
      app.name.toLowerCase().includes(q) ||
      (app.description ? app.description.toLowerCase().includes(q) : false) ||
      (app.technical_name ? app.technical_name.toLowerCase().includes(q) : false) ||
      (app.folder_name ? app.folder_name.toLowerCase().includes(q) : false)
    const matchesFilter = filter === 'all' || isFavorite(app.id)
    return matchesSearch && matchesFilter
  })

  const handleLaunch = useCallback(async (app: AppItem) => {
    setLaunchingId(app.id)
    setLaunchError('')

    const currentMode = usePreferencesStore.getState().launchMode

    try {
      console.log('[AppsPage] Launch mode from store (getState):', currentMode)
      const res = await api.post<LaunchResult>('/launch/session', {
        app_id: app.id,
        app_name: app.technical_name || app.name,
        app_type: app.app_type === 'desktop' ? 2 : 1,
        cmd_args: app.path || '',
        mode: currentMode,
      })

      const { mode, download_url, file_name } = res.data

      if (mode === 'native' && download_url) {
        const response = await api.get(download_url, {
          responseType: 'blob',
        })
        const blob = new Blob([response.data], { type: 'application/octet-stream' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = file_name || 'launch.flexilabs'
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } else if (mode === 'html5') {
        const html5Params = {
          server: res.data.server || '',
          username: res.data.username || '',
          password_encrypted: res.data.password_encrypted || '',
          initial_program: res.data.initial_program,
          working_dir: res.data.working_dir,
          width: res.data.width || 1024,
          height: res.data.height || 768,
          dpi: res.data.dpi || 96,
          app_name: app.name,
          guacamole_host: res.data.guacamole_host,
        }
        localStorage.setItem('html5_launch_params', JSON.stringify(html5Params))
        window.open('/html5-launch.html', '_blank')
      } else if (mode === 'redirect' && res.data.redirect_url) {
        window.open(res.data.redirect_url, '_blank', 'noopener')
      } else {
        setLaunchError('Launch mode not supported')
      }
    } catch (err: any) {
      const msg = err.response?.data?.detail || 'Error launching application'
      setLaunchError(msg)
    } finally {
      setLaunchingId(null)
    }
  }, [])

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Typography variant="h5" component="h2">
          {t('apps.title')}
        </Typography>
        <IconButton size="small" onClick={() => refetch()} title={t('apps.refresh')}>
          <RefreshIcon fontSize="small" />
        </IconButton>
        <Typography
          variant="caption"
          sx={{
            px: 1,
            py: 0.3,
            borderRadius: 1,
            bgcolor: launchMode === 'html5' ? 'success.light' : 'info.light',
            color: launchMode === 'html5' ? 'success.dark' : 'info.dark',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}
        >
          {launchMode}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder={t('apps.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            flex: 1,
            minWidth: 200,
            '& .MuiOutlinedInput-root': {
              bgcolor: glassmorphism ? 'rgba(255,255,255,0.65)' : undefined,
              backdropFilter: glassmorphism ? 'blur(10px)' : undefined,
              WebkitBackdropFilter: glassmorphism ? 'blur(10px)' : undefined,
            },
          }}
        />
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(_, val) => val && setFilter(val)}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              bgcolor: glassmorphism ? 'rgba(255,255,255,0.65)' : undefined,
              backdropFilter: glassmorphism ? 'blur(10px)' : undefined,
              WebkitBackdropFilter: glassmorphism ? 'blur(10px)' : undefined,
            },
            '& .Mui-selected': {
              bgcolor: glassmorphism ? 'rgba(255,255,255,0.85)' : undefined,
            },
          }}
        >
          <ToggleButton value="all">{t('apps.all')}</ToggleButton>
          <ToggleButton value="favorites">{t('apps.favorites')}</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {(error as any) && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {(error as any).message || t('common.error')}
        </Alert>
      )}

      {launchError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setLaunchError('')}>
          {launchError}
        </Alert>
      )}

      <Grid container spacing={isSmallSize ? 2 : 3}>
        {filteredApps.map((app) => (
          <Grid
            item
            xs={12}
            sm={isSmallSize ? 4 : 6}
            md={isSmallSize ? 3 : 4}
            lg={isSmallSize ? 2 : 3}
            key={app.id}
          >
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                cursor: launchingId === app.id ? 'default' : 'pointer',
                position: 'relative',
                opacity: launchingId === app.id ? 0.6 : 1,
                transition: 'all 0.2s ease',
                bgcolor: glassmorphism ? 'rgba(255,255,255,0.65)' : undefined,
                backdropFilter: glassmorphism ? 'blur(10px)' : undefined,
                WebkitBackdropFilter: glassmorphism ? 'blur(10px)' : undefined,
                border: glassmorphism ? '1px solid rgba(255,255,255,0.35)' : undefined,
                boxShadow: glassmorphism ? '0 4px 16px rgba(0,0,0,0.08)' : undefined,
                '&:hover': {
                  bgcolor: glassmorphism ? 'rgba(255,255,255,0.85)' : 'action.hover',
                },
              }}
              onClick={() => {
                if (launchingId !== app.id) handleLaunch(app)
              }}
            >
              <CardContent sx={{ flexGrow: 1, textAlign: 'center', position: 'relative' }}>
                <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                  {isFavorite(app.id) ? (
                    <StarIcon
                      sx={{ color: 'warning.main', fontSize: isSmallSize ? 16 : 20 }}
                      onClick={(e) => {
                        e.stopPropagation()
                        toggleFavorite(app.id)
                      }}
                    />
                  ) : (
                    <StarBorderIcon
                      sx={{ color: 'text.disabled', fontSize: isSmallSize ? 16 : 20 }}
                      onClick={(e) => {
                        e.stopPropagation()
                        toggleFavorite(app.id)
                      }}
                    />
                  )}
                </Box>
                {(iconMode === 'actual' && app.icon_url) || iconMode === 'generic' ? (
                  <Box sx={{ mb: isSmallSize ? 1 : 2, display: 'flex', justifyContent: 'center' }}>
                    {iconMode === 'actual' && app.icon_url ? (
                      <Box
                        component="img"
                        src={app.icon_url}
                        alt={app.name}
                        sx={{
                          height: isSmallSize ? 48 : 64,
                          width: isSmallSize ? 48 : 64,
                          objectFit: 'contain',
                        }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none'
                        }}
                      />
                    ) : (
                      app.app_type === 'desktop' ? (
                        <MonitorOutlined sx={{ fontSize: isSmallSize ? 48 : 64, color: 'primary.main' }} />
                      ) : app.app_type === 'content' ? (
                        <LinkIcon sx={{ fontSize: isSmallSize ? 48 : 64, color: 'secondary.main' }} />
                      ) : (
                        <AppsOutlined sx={{ fontSize: isSmallSize ? 48 : 64, color: 'secondary.main' }} />
                      )
                    )}
                  </Box>
                ) : null}
                <Typography variant={isSmallSize ? 'body1' : 'h6'} component="div" gutterBottom>
                  {app.name}
                </Typography>
              </CardContent>

              {launchingId === app.id && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CircularProgress size={isSmallSize ? 20 : 40} />
                </Box>
              )}
            </Card>
          </Grid>
        ))}
      </Grid>

      {filteredApps.length === 0 && !error && (
        <Alert severity="info" sx={{ mt: 4 }}>
          {t('apps.noApps')}
        </Alert>
      )}
    </Box>
  )
}
